import ubicacionService from '../services/ubicacionService';
import sucVentaService from '../services/sucVentaService';
import {
  debeImprimirAutomaticoPos,
  debeImprimirTicketEscPos,
  NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
  type ModoImpresionFacturaVenta,
  type SucVentaImpresionTicket,
} from './posImpresionConfig';

export type SucVentaMapEntry = SucVentaImpresionTicket & {
  generaRemito?: string;
  nroSucRemito?: number;
};

export interface ContextoImpresionFacturaInput {
  nroSuc?: number | null;
  nroTipoFormulario?: string | null;
  nroUbicacionOrigen?: number | null;
  ubicacion?: string | null;
}

export interface CandidatoSucVentaFactura {
  nroSuc: number;
  nroTipoFormulario: string;
  entry: SucVentaMapEntry;
}

export function parseNroUbicacionDesdeEtiqueta(ubicacion?: string | null): number | null {
  const m = String(ubicacion ?? '').trim().match(/\((\d+)\)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function leerNroUbicacionOrigenFactura(input: ContextoImpresionFacturaInput): number | null {
  const direct = Number(input.nroUbicacionOrigen) || 0;
  if (direct > 0) return direct;
  return parseNroUbicacionDesdeEtiqueta(input.ubicacion);
}

export function normalizarNroTipoFormularioSucVenta(nroTipoFormulario?: string | null): string {
  const t = String(nroTipoFormulario ?? '').trim();
  return t || NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA;
}

export function claveSucVenta(nroSuc: number, nroTipoFormulario: string): string {
  return `${nroSuc}|${normalizarNroTipoFormularioSucVenta(nroTipoFormulario)}`;
}

export function resolverNroSucDesdeUbicacion(
  nroUbicacion: number | null | undefined,
  ubicacionPorNro: Record<number, number>,
): number | null {
  const nro = Number(nroUbicacion) || 0;
  if (nro <= 0) return null;
  const ns = ubicacionPorNro[nro];
  return ns && ns > 0 ? ns : null;
}

/** Sucursales a probar: primero la de la ubicación, luego la del comprobante. */
export function enumerarNroSucCandidatos(
  input: ContextoImpresionFacturaInput,
  ubicacionPorNro: Record<number, number>,
): number[] {
  const candidatos: number[] = [];
  const push = (nroSuc: number | null | undefined) => {
    const n = Number(nroSuc) || 0;
    if (n > 0 && !candidatos.includes(n)) candidatos.push(n);
  };

  const nroUbic = leerNroUbicacionOrigenFactura(input);
  push(resolverNroSucDesdeUbicacion(nroUbic, ubicacionPorNro));
  push(input.nroSuc);
  return candidatos;
}

function enumerarTiposFormularioCandidatos(nroTipoFormulario?: string | null): string[] {
  const tipos: string[] = [];
  const push = (tipo: string) => {
    const t = String(tipo ?? '').trim();
    if (t && !tipos.includes(t)) tipos.push(t);
  };

  push(normalizarNroTipoFormularioSucVenta(nroTipoFormulario));
  if (!tipos.includes(NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA)) {
    push(NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA);
  }
  return tipos;
}

/** Todas las combinaciones sucursal + tipo formulario encontradas en el mapa. */
export function enumerarCandidatosSucVentaFactura(
  input: ContextoImpresionFacturaInput,
  sucVentaPorClave: Record<string, SucVentaMapEntry>,
  ubicacionPorNro: Record<number, number>,
): CandidatoSucVentaFactura[] {
  const resultados: CandidatoSucVentaFactura[] = [];
  const vistos = new Set<string>();

  for (const nroSuc of enumerarNroSucCandidatos(input, ubicacionPorNro)) {
    for (const nroTipoFormulario of enumerarTiposFormularioCandidatos(input.nroTipoFormulario)) {
      const clave = claveSucVenta(nroSuc, nroTipoFormulario);
      if (vistos.has(clave)) continue;
      vistos.add(clave);
      const entry = sucVentaPorClave[clave];
      if (entry) {
        resultados.push({ nroSuc, nroTipoFormulario, entry });
      }
    }
  }

  return resultados;
}

/** Sucursal de venta según ubicación de la factura (prioriza depósito sobre nroSuc del comprobante). */
export function resolverNroSucImpresionFactura(
  input: ContextoImpresionFacturaInput,
  ubicacionPorNro: Record<number, number>,
): number {
  const candidatos = enumerarNroSucCandidatos(input, ubicacionPorNro);
  return candidatos[0] ?? 0;
}

/** Primer SucVenta encontrado (cualquier config de impresión/remito). */
export function resolverSucVentaFactura(
  input: ContextoImpresionFacturaInput,
  sucVentaPorClave: Record<string, SucVentaMapEntry>,
  ubicacionPorNro: Record<number, number>,
): SucVentaMapEntry | null {
  return enumerarCandidatosSucVentaFactura(input, sucVentaPorClave, ubicacionPorNro)[0]?.entry ?? null;
}

/** SucVenta con Imprime = S (PDF o ticket según tipo impresora). */
export function resolverSucVentaImpresionFactura(
  input: ContextoImpresionFacturaInput,
  sucVentaPorClave: Record<string, SucVentaMapEntry>,
  ubicacionPorNro: Record<number, number>,
): CandidatoSucVentaFactura | null {
  return (
    enumerarCandidatosSucVentaFactura(input, sucVentaPorClave, ubicacionPorNro).find((c) =>
      debeImprimirAutomaticoPos(c.entry),
    ) ?? null
  );
}

/** SucVenta con ticketera EPSON habilitada. */
export function resolverSucVentaTicketFactura(
  input: ContextoImpresionFacturaInput,
  sucVentaPorClave: Record<string, SucVentaMapEntry>,
  ubicacionPorNro: Record<number, number>,
): CandidatoSucVentaFactura | null {
  return (
    enumerarCandidatosSucVentaFactura(input, sucVentaPorClave, ubicacionPorNro).find((c) =>
      debeImprimirTicketEscPos(c.entry),
    ) ?? null
  );
}

/**
 * Modo de impresión según SucVenta de la factura.
 * - ticketera: menú PDF/ticket en listado; auto-ticket al emitir en POS.
 * - pdf: abre PDF directo (láser u otras con Imprime=S).
 */
export function resolverModoImpresionFactura(
  input: ContextoImpresionFacturaInput,
  sucVentaPorClave: Record<string, SucVentaMapEntry>,
  ubicacionPorNro: Record<number, number>,
): ModoImpresionFacturaVenta | null {
  const candidatos = enumerarCandidatosSucVentaFactura(input, sucVentaPorClave, ubicacionPorNro);
  if (candidatos.some((c) => debeImprimirTicketEscPos(c.entry))) {
    return 'ticketera';
  }
  if (candidatos.some((c) => debeImprimirAutomaticoPos(c.entry))) {
    return 'pdf';
  }
  return null;
}

function normalizarItemSucVentaMapa(raw: Record<string, unknown>): {
  nroSuc: number;
  nroTipoFormulario: string;
  entry: SucVentaMapEntry;
} | null {
  const nroSuc = Number(raw.nroSuc ?? raw.NroSuc) || 0;
  const nroTipoFormulario = String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? '').trim();
  if (!nroSuc || !nroTipoFormulario) return null;

  return {
    nroSuc,
    nroTipoFormulario,
    entry: {
      imprime: String(raw.imprime ?? raw.Imprime ?? 'N'),
      tipoImpresora: String(raw.tipoImpresora ?? raw.TipoImpresora ?? '').trim() || undefined,
      generaRemito: String(raw.generaRemito ?? raw.GeneraRemito ?? 'N'),
      nroSucRemito: Number(raw.nroSucRemito ?? raw.NroSucRemito) || undefined,
    },
  };
}

export async function cargarMapaUbicacionNroSuc(): Promise<Record<number, number>> {
  const map: Record<number, number> = {};
  let page = 1;
  const pageSize = 20;
  let hasMore = true;
  while (hasMore) {
    const res = await ubicacionService.getUbicacionesPaged({ pageNumber: page, pageSize });
    for (const u of res.items) {
      const nroUbicacion = Number((u as { nroUbicacion?: number; NroUbicacion?: number }).nroUbicacion
        ?? (u as { NroUbicacion?: number }).NroUbicacion) || 0;
      const nroSuc = Number((u as { nroSuc?: number; NroSuc?: number }).nroSuc
        ?? (u as { NroSuc?: number }).NroSuc) || 0;
      if (nroUbicacion > 0 && nroSuc > 0) {
        map[nroUbicacion] = nroSuc;
      }
    }
    hasMore = res.items.length === pageSize && page * pageSize < (res.totalCount ?? 0);
    page++;
  }
  return map;
}

export async function cargarMapaSucVentaPorClave(): Promise<Record<string, SucVentaMapEntry>> {
  const map: Record<string, SucVentaMapEntry> = {};
  let page = 1;
  const pageSize = 20;
  let hasMore = true;
  while (hasMore) {
    const res = await sucVentaService.getSucVentas({ pageNumber: page, pageSize });
    for (const sv of res.items) {
      const normalizado = normalizarItemSucVentaMapa(sv as unknown as Record<string, unknown>);
      if (!normalizado) continue;
      map[claveSucVenta(normalizado.nroSuc, normalizado.nroTipoFormulario)] = normalizado.entry;
    }
    hasMore = res.items.length === pageSize && page * pageSize < (res.totalCount ?? 0);
    page++;
  }
  return map;
}

export interface ContextoSucVentaImpresion {
  nroSuc: number;
  nroTipoFormulario: string;
}

export function resolverContextoSucVentaImpresion(
  input: ContextoImpresionFacturaInput,
  ubicacionPorNro: Record<number, number>,
  sucVentaPorClave: Record<string, SucVentaMapEntry> = {},
  modo: 'ticket' | 'impresion' | 'cualquiera' = 'cualquiera',
  nroTipoFormularioDefault = NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
): ContextoSucVentaImpresion {
  const inputConDefault: ContextoImpresionFacturaInput = {
    ...input,
    nroTipoFormulario: input.nroTipoFormulario ?? nroTipoFormularioDefault,
  };

  if (modo === 'ticket') {
    const ticket = resolverSucVentaTicketFactura(inputConDefault, sucVentaPorClave, ubicacionPorNro);
    if (ticket) return { nroSuc: ticket.nroSuc, nroTipoFormulario: ticket.nroTipoFormulario };
  }

  if (modo === 'impresion' || modo === 'ticket') {
    const impresion = resolverSucVentaImpresionFactura(inputConDefault, sucVentaPorClave, ubicacionPorNro);
    if (impresion) return { nroSuc: impresion.nroSuc, nroTipoFormulario: impresion.nroTipoFormulario };
  }

  const candidato = enumerarCandidatosSucVentaFactura(inputConDefault, sucVentaPorClave, ubicacionPorNro)[0];
  if (candidato) {
    return { nroSuc: candidato.nroSuc, nroTipoFormulario: candidato.nroTipoFormulario };
  }

  return {
    nroSuc: resolverNroSucImpresionFactura(inputConDefault, ubicacionPorNro),
    nroTipoFormulario: normalizarNroTipoFormularioSucVenta(inputConDefault.nroTipoFormulario),
  };
}

export function claveCacheSucVenta(ctx: ContextoSucVentaImpresion): string {
  return claveSucVenta(ctx.nroSuc, ctx.nroTipoFormulario);
}

export function facturaInputDesdeFacturaVenta(factura: {
  nroSuc?: number;
  nroTipoFormulario?: string;
  nroUbicacionOrigen?: number;
  ubicacion?: string;
}): ContextoImpresionFacturaInput {
  return {
    nroSuc: factura.nroSuc,
    nroTipoFormulario: factura.nroTipoFormulario,
    nroUbicacionOrigen: factura.nroUbicacionOrigen,
    ubicacion: factura.ubicacion,
  };
}
